import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { Menu, Moon, Sun } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { DesktopSidebar, MobileDrawer, Brand } from "@/components/Sidebar";
import { NotificationBell } from "@/components/NotificationBell";
import { TicketButton } from "@/components/support/TicketCenter";
import { ROLE_LABEL } from "@/lib/api";

const initials = (n = "") => n.split(" ").filter(Boolean).slice(0, 2).map((s) => s[0]).join("").toUpperCase();

function Topbar({ onMenu, dark, onTheme }) {
  const { user, office } = useAuth();
  return (
    <header data-testid="app-topbar" className="glass-panel sticky top-0 lg:top-3 z-40 h-14 lg:h-16 flex items-center gap-3 px-4 lg:px-5 rounded-none lg:rounded-2xl border-x-0 border-t-0 lg:border lg:mx-6 lg:mt-3">
      <button data-testid="mobile-menu-toggle" aria-label="Menüyü aç" onClick={onMenu} className="lg:hidden p-2 -ml-2 rounded-lg hover:bg-white/70">
        <Menu className="w-5 h-5 text-slate-800" />
      </button>
      <div className="lg:hidden"><Brand /></div>
      <div className="hidden lg:block min-w-0">
        <div className="text-[11px] uppercase tracking-wider text-slate-400 font-semibold">{office ? "Ofis" : "Platform"}</div>
        <div data-testid="topbar-context" className="text-sm font-semibold text-slate-800 truncate">{office?.name || "EmlakPlus Yönetim"}</div>
      </div>
      <div className="ml-auto flex items-center gap-2">
        {user.office_id && <TicketButton />}
        <button data-testid="theme-toggle" onClick={onTheme} aria-label={dark ? "Açık temaya geç" : "Karanlık temaya geç"} title={dark ? "Açık tema" : "Karanlık tema"}
          className="w-8 h-8 rounded-lg hover:bg-white/70 flex items-center justify-center text-slate-600">
          {dark ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
        </button>
        <NotificationBell />
        <div data-testid="topbar-user" className="hidden md:flex items-center gap-2.5 pl-2 ml-1 border-l border-slate-200/70">
          <div className="w-9 h-9 rounded-full bg-emerald-700 text-white text-xs font-bold flex items-center justify-center">{initials(user.full_name)}</div>
          <div className="leading-tight">
            <div className="text-sm font-semibold text-slate-900 max-w-[160px] truncate">{user.full_name}</div>
            <div className="text-[11px] text-slate-500">{ROLE_LABEL[user.role]}</div>
          </div>
        </div>
      </div>
    </header>
  );
}

export default function AppShell({ children }) {
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem("sidebar-collapsed") === "1");
  const [dark, setDark] = useState(() => localStorage.getItem("panel-theme") === "dark");

  useEffect(() => {
    document.body.classList.toggle("theme-dark", dark);
    localStorage.setItem("panel-theme", dark ? "dark" : "light");
    return () => document.body.classList.remove("theme-dark");
  }, [dark]);

  useEffect(() => {
    document.body.classList.add("app-shell");
    return () => document.body.classList.remove("app-shell");
  }, []);
  useEffect(() => setOpen(false), [location.pathname]);
  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [open]);
  const toggle = () => setCollapsed((c) => { localStorage.setItem("sidebar-collapsed", c ? "0" : "1"); return !c; });

  return (
    <div className="min-h-screen flex">
      <DesktopSidebar collapsed={collapsed} onToggle={toggle} />
      <MobileDrawer open={open} onClose={() => setOpen(false)} />
      <div className="flex-1 min-w-0 flex flex-col">
        <Topbar onMenu={() => setOpen(true)} dark={dark} onTheme={() => setDark((d) => !d)} />
        <main className="flex-1 min-w-0 px-4 py-5 sm:px-6 lg:px-8 lg:py-7 overflow-x-clip">
          {children}
        </main>
      </div>
    </div>
  );
}
